// pages/Roles/EditRole.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function EditRole({ role, onClose, onUpdate }) {
  const theme = useTheme();
  const [modules, setModules] = useState([]);
  const [formData, setFormData] = useState({
    name: role?.name || '',
    description: role?.description || '',
    permissions: role?.permissions || []
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingModules, setLoadingModules] = useState(true);

  useEffect(() => {
    if (role) {
      fetchModules();
    }
  }, [role]);

  const fetchModules = async () => {
    try {
      setLoadingModules(true);
      const response = await axiosInstance.get('/api/modules');
      
      if (response.data.success) {
        const responseData = typeof response.data.data === 'string' 
          ? JSON.parse(response.data.data) 
          : response.data.data;
        
        setModules(responseData.modules || []);
        
        const existingPerms = role.permissions || [];
        const allPermissions = [];
        
        const flattenSubmodules = (mods) => {
          mods.forEach(mod => {
            if (mod.submodules && mod.submodules.length > 0) {
              mod.submodules.forEach(submod => {
                const existing = existingPerms.find(p => p.module_id === submod.id);
                allPermissions.push({
                  module_id: submod.id,
                  module_name: submod.name,
                  can_view: true,
                  can_add: existing?.can_add || false,
                  can_edit: existing?.can_edit || false,
                  can_delete: existing?.can_delete || false
                });
              });
            }
          });
        };
        flattenSubmodules(responseData.modules || []);
        
        setFormData(prev => ({
          ...prev,
          permissions: allPermissions
        }));
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      setError("Failed to load modules");
    } finally {
      setLoadingModules(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (moduleId, permissionType, value) => {
    if (permissionType === 'can_view') return;
    
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(perm => 
        perm.module_id === moduleId 
          ? { ...perm, [permissionType]: value }
          : perm
      )
    }));
  };

  const handleSelectAll = (permissionType, value) => {
    if (permissionType === 'can_view') return;
    
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(perm => ({
        ...perm,
        [permissionType]: value
      }))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    if (!formData.name) {
      setError("Role name is required");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.put(`/api/roles/${role.id}`, {
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions
      });
      
      if (response.data.success) {
        setSuccess("Role updated successfully!");
        setTimeout(() => {
          onUpdate();
        }, 1500);
      } else {
        setError(response.data.message || "Failed to update role");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      setError(error.response?.data?.message || "Error updating role. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderModulePermissions = (module, level = 0) => {
    if (level === 0) {
      return (
        <React.Fragment key={module.id}>
          <Box
            component="tr"
            sx={{
              backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#e9ecef",
            }}
          >
            <td colSpan="5" style={{ paddingLeft: '10px', fontWeight: 'bold' }}>
              <i className={`fa ${module.icon} me-2`}></i>
              {module.name}
              <Chip label="Parent Module" size="small" sx={{ ml: 1 }} color="default" />
            </td>
          </Box>
          {module.submodules && module.submodules.map(submodule => 
            renderModulePermissions(submodule, level + 1)
          )}
        </React.Fragment>
      );
    }
    
    const permission = formData.permissions.find(p => p.module_id === module.id);
    if (!permission) return null;

    return (
      <tr key={module.id}>
        <td style={{ paddingLeft: `${level * 20 + 10}px` }}>
          {module.name}
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={true}
            disabled={true}
            className="form-check-input"
            title="View permission is enabled by default for all roles"
          />
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={permission.can_add}
            onChange={(e) => handlePermissionChange(module.id, 'can_add', e.target.checked)}
            className="form-check-input"
            disabled={role?.is_system_role}
          />
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={permission.can_edit}
            onChange={(e) => handlePermissionChange(module.id, 'can_edit', e.target.checked)}
            className="form-check-input"
            disabled={role?.is_system_role}
          />
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={permission.can_delete}
            onChange={(e) => handlePermissionChange(module.id, 'can_delete', e.target.checked)}
            className="form-check-input"
            disabled={role?.is_system_role}
          />
        </td>
      </tr>
    );
  };

  if (!role) {
    return (
      <Box p={3} textAlign="center">
        <Typography>No role data available</Typography>
        <Button variant="contained" onClick={onClose} sx={{ mt: 2 }}>
          Close
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.paper" }}>
      {/* Modal Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" color="text.primary">
          Edit Role: <strong>{role.name}</strong>
        </Typography>
        <IconButton onClick={onClose} disabled={isLoading} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Modal Body */}
      <Box 
        sx={{ 
          p: 3, 
          maxHeight: '70vh', 
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? '#475569' : '#cbd5e1',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#64748b' : '#94a3b8',
            },
          },
        }}
      >
        {role.is_system_role && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <i className="fa fa-info-circle me-2"></i>
            <strong>Global System Role:</strong> Permissions for global system roles (Editor, Viewer) cannot be modified. You can only view their current permissions.
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          <i className="fa fa-info-circle me-2"></i>
          <strong>Note:</strong> View permission is enabled by default for all submodules. 
          You can configure Add, Edit, and Delete permissions for each submodule.
        </Alert>

        <form onSubmit={handleSubmit}>
          <Box display="flex" gap={2} mb={3}>
            <TextField
              fullWidth
              label="Role Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={isLoading || role.is_system_role}
              size="small"
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              disabled={isLoading || role.is_system_role}
              size="small"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" mb={2} color="text.primary">
            Module Permissions
          </Typography>

          {loadingModules ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress size={30} />
              <Typography ml={2}>Loading modules...</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                overflowX: "auto",
                "& table": {
                  width: "100%",
                  borderCollapse: "collapse",
                  border: `1px solid ${theme.palette.divider}`,
                },
                "& th, & td": {
                  padding: "12px",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.text.primary,
                },
                "& th": {
                  backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                },
                "& tbody tr:hover": {
                  backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f8fafc",
                },
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Module / Submodule</th>
                    <th className="text-center" style={{ width: '100px' }}>
                      View
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        (Always On)
                      </Typography>
                    </th>
                    <th className="text-center" style={{ width: '100px' }}>
                      Add
                      {!role.is_system_role && (
                        <>
                          <br />
                          <Button
                            size="small"
                            onClick={() => handleSelectAll('can_add', true)}
                            sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                          >
                            All
                          </Button>
                          {' | '}
                          <Button
                            size="small"
                            onClick={() => handleSelectAll('can_add', false)}
                            sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                          >
                            None
                          </Button>
                        </>
                      )}
                    </th>
                    <th className="text-center" style={{ width: '100px' }}>
                      Edit
                      {!role.is_system_role && (
                        <>
                          <br />
                          <Button
                            size="small"
                            onClick={() => handleSelectAll('can_edit', true)}
                            sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                          >
                            All
                          </Button>
                          {' | '}
                          <Button
                            size="small"
                            onClick={() => handleSelectAll('can_edit', false)}
                            sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                          >
                            None
                          </Button>
                        </>
                      )}
                    </th>
                    <th className="text-center" style={{ width: '100px' }}>
                      Delete
                      {!role.is_system_role && (
                        <>
                          <br />
                          <Button
                            size="small"
                            onClick={() => handleSelectAll('can_delete', true)}
                            sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                          >
                            All
                          </Button>
                          {' | '}
                          <Button
                            size="small"
                            onClick={() => handleSelectAll('can_delete', false)}
                            sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto' }}
                          >
                            None
                          </Button>
                        </>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map(module => renderModulePermissions(module))}
                </tbody>
              </table>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <i className="fa fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <i className="fa fa-check me-2"></i>
              {success}
            </Alert>
          )}
        </form>
      </Box>

      {/* Modal Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isLoading}
        >
          {role.is_system_role ? 'Close' : 'Cancel'}
        </Button>
        {!role.is_system_role && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || loadingModules}
          >
            {isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              <>
                <i className="fa fa-save me-2"></i>
                Update Role
              </>
            )}
          </Button>
        )}
      </Box>
    </Box>
  );
}